import { useEffect, useLayoutEffect, useState } from "react";
import { Link, Head } from "@inertiajs/react";
import ApplicationLogo from "@/Components/Global/ApplicationLogo";
import GuestHeader from "@/Components/Global/GuestHeader";
import PrimaryButton from "@/Components/Global/PrimaryButton";
import Review from "@/Components/Guest/Review";
import ContactForm from "@/Components/Guest/ContactForm";
import GuestFooter from "@/Components/Global/GuestFooter";
import isMobile from "@/utils/isMobile";
import MobileGuestHeader from "@/Components/Global/MobileGuestHeader";
import { usePage } from "@inertiajs/react";
import Study from "@/Components/Guest/Study";

export default function Welcome() {
    const [isMobileView, setIsMobileView] = useState(isMobile());
    const urlParams =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
            : null;
    const { url } = usePage();

    useLayoutEffect(() => {
        const handleResize = () => {
            setIsMobileView(isMobile());
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const HeaderComponent = isMobileView ? MobileGuestHeader : GuestHeader;

    const scrollToElement = (elementId: string, offset: number = 200) => {
        const element = document.getElementById(elementId);
        if (element) {
            const rect = element.getBoundingClientRect();
            const offsetTop = window.pageYOffset + rect.top;
            window.scrollTo({
                top: offsetTop - offset,
                behavior: "smooth",
            });
        }
    };
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const scrollToId = urlParams.get("scrollTo");

        if (scrollToId) {
            // Optionally wait a tiny bit to ensure the element is rendered:
            setTimeout(() => {
                const element = document.getElementById(scrollToId);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const offsetValue =
                        scrollToId == "restrictedRegistration" ? 150 : 0;
                    const offsetTop =
                        window.pageYOffset + rect.top - offsetValue;
                    window.scrollTo({
                        top: offsetTop,
                        behavior: "smooth",
                    });

                    const newUrl =
                        window.location.origin + window.location.pathname;
                    window.history.replaceState({}, document.title, newUrl);
                }
            }, 100);
        }
    }, []);

    return (
        <>
            <Head title="Welcome" />
            <HeaderComponent />
            <div id="bannerSection" className="dark:bg-gray-900">
                <div className="relative max-w-[1440px] m-auto min-h-[300px]">
                    <div className="h-1/2 w-1/2 lg:h-[300px] lg:w-[300px]   xl:h-[350px] xl:w-[350px] flex justify-center items-center absolute inset-0 m-auto">
                        <ApplicationLogo className="h-full fill-current text-gray-800 dark:text-gray-200" />
                    </div>
                    {!isMobileView ? (
                        <div className="absolute bottom-[-25px] left-1/2 transform -translate-x-1/2 flex justify-center z-10 space-x-4 lg:w-[400px] xl:w-[600px]">
                            <div className="flex relative xl:w-full">
                                <img
                                    className="absolute -top-10 -left-[155px]"
                                    src="/assets/images/icons/yellowBulp.svg"
                                    alt="Yellow Bulb"
                                />
                                <div className="flex w-full ">
                                    <Link
                                        href="/login"
                                        className="m-auto min-w-[250px]"
                                    >
                                        <PrimaryButton className="text-white text-[18px] w-full">
                                            LOGIN
                                        </PrimaryButton>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="z-10 absolute bottom-[-15px] flex justify-center items-center w-full">
                            <div className="relative w-[70%] max-w-[600px]">
                                <img
                                    className="w-[70px] max-w-[150px] absolute left-[-15px] transform -translate-x-1/2 bottom-[-40px]"
                                    src="/assets/images/icons/yellowBulp.svg"
                                    alt="Yellow Bulb"
                                />
                                <div className="flex  mt-12">
                                    <Link
                                        href="/login"
                                        className="m-auto w-[50vw] xsm:w-[250px]"
                                    >
                                        <PrimaryButton className="text-white w-full text-[13px] py-1">
                                            LOGIN
                                        </PrimaryButton>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}

                    <img
                        src="/assets/images/icons/yellowBrick.svg"
                        alt="Yellow Brick"
                        className="hidden md:block"
                    />
                    <img
                        src="/assets/images/icons/purpleCircle.svg"
                        alt="Purple Circle"
                        className="w-[15vw] lg:w-auto absolute top-0 right-0"
                    />
                    <img
                        src="/assets/images/icons/greenRect.svg"
                        alt="Green Rect"
                        className="w-[50vw] lg:w-auto absolute bottom-0 right-0"
                    />
                </div>
            </div>

            <div
                id="aboutUsSection"
                className="bg-secondary-light dark:bg-background-dark dark:text-text-dark"
            >
                <div className="max-w-[1000px] m-auto p-10">
                    <div className="md:h-[50px]" />
                    <h2 className="text-xl md:text-3xl font-bold mt-20 mb-3">
                        ABOUT US
                    </h2>
                    <div className="relaitive  flex flex-col md:flex-row items-center mb-24 md:mb-12">
                        <div className="md:w-3/5 bg-gray-100 dark:bg-secondary-dark p-6 rounded-md text-sm md:text-base">
                            <p>
                                Research has shown that emotional biases in our
                                memory and depressive mood states are tightly
                                linked. This has led to the development of
                                memory reshaping for depression (MemReD), a
                                training programme which consists of two
                                components: 1) Memory flexibility (MemFlex) and
                                2) Blame Rebalancing. Both components highlight
                                the ways in which memory can become unhelpful
                                and provides guided practice with more flexible
                                and effective ways of thinking about your
                                personal past to correct these unhelpful memory
                                habits. You will then be encouraged to apply
                                more helpful ways of thinking on a day-to-day
                                basis.
                            </p>
                            <br />
                            <p>
                                The development of MemReD at King’s College
                                London was funded by the UK Medical Research
                                Council and led by Professor Roland Zahn,
                                Professor of Mood Disorders & Consultant
                                Psychiatrist at the Maudsley National Service
                                for Affective Disorders. It was developed in
                                collaboration with the University of Melbourne
                                (Dr Caitlin Hitchcock), the University of
                                Cambridge (Professor Tim Dalgleish), and the
                                D’Or Institute for Research and Education in Rio
                                de Janeiro (Dr Jorge Moll).
                            </p>
                            <br />
                            <p>
                                We also credit the design and development team
                                Rose Chaykin, Daniel Kiss, Csenge Csery, and the
                                lived experience user advisory group, including
                                Sarah Markham and Qiuyue Zhang, for their
                                essential contributions
                            </p>
                            <br />
                            <p>
                                MemFlex was developed by Dr Caitlin Hitchcock,
                                Clinical Psychologist at the University of
                                Melbourne, together with Professor Tim
                                Dalgleish, Professor at the University of
                                Cambridge and Clinical Psychologist and Director
                                of the Cambridge Clinical Research Centre in
                                Affective Disorders.
                            </p>
                        </div>
                        <div className="relative md:sticky w-full  md:w-2/5 flex justify-center mt-0">
                            <img
                                src="/assets/images/icons/blueBulp.svg"
                                alt="Blue Bulb"
                                className="h-28 md:h-60 absolute md:sticky bottom-[-80px] -right-4"
                            />
                        </div>
                    </div>

                    <div className="relative flex flex-col md:flex-row items-center">
                        <div className="md:w-2/5 flex justify-center order-2 md:order-1 mt-0 mr-5">
                            <img
                                src="/assets/images/icons/groupBulp.svg"
                                alt="Group Bulb"
                                className="md:h-60"
                            />
                        </div>
                        <div
                            id="restrictedRegistration"
                            className="mb-5 md:mb-0 md:w-3/5 bg-gray-100 dark:bg-secondary-dark p-6 rounded-md text-sm md:text-base order-1 md:order-2"
                        >
                            <p>
                                <span className="font-bold">
                                    The registration for MemReD is only by
                                    invitation of a psychologist within our team{" "}
                                </span>
                                who will need to support you during the
                                programme. Currently MemReD is only available to
                                UK residents who take part in our clinical trial
                                at King’s College London. If you would like to
                                take part, please fill in our pre-screening form{" "}
                                <Link href="">here</Link>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div id="reviewsSection" className="dark:bg-gray-900">
                <div className="relative max-w-[1440px] m-auto flex justify-between items-center">
                    <div className="flex-grow text-center mt-16">
                        {/* <h1 className="text-xl md:text-3xl md:mt-16 font-bold uppercase dark:text-text-dark">
                            Reviews
                        </h1>
                        <div className="p-10 md:p-16 md:w-2/3 m-auto">
                            <Review />
                        </div> */}
                        <h1 className="text-xl md:text-3xl font-bold uppercase dark:text-text-dark">
                            WANT TO TRY MEMRED?
                        </h1>
                        <div className="p-10 md:p-16 md:w-2/3 m-auto">
                            <Study />
                        </div>
                    </div>
                </div>
            </div>
            <div
                id="contactSection"
                className="bg-secondary-light dark:bg-background-dark  h-auto py-12"
            >
                <div className="max-w-[1440px] mx-auto">
                    <ContactForm />
                </div>
            </div>
            <div
                id="footerSection"
                className="bg-background-light dark:bg-secondary-dark"
            >
                <GuestFooter />
            </div>
        </>
    );
}
